import {
  Alert,
  AlertIcon,
  Avatar,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Grid,
  GridItem,
  Image,
  Input,
  Tag,
  Text,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { DetailField } from '../../../components/DetailField'
import { FormModal } from '../../../components/FormModal'
import { FormSelect } from '../../../components/Select'
import { EXHIBITION_STATUS, StatusBadge, StatusMap } from '../../../components/StatusBadge'
import { useAppToast } from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import { IContestApplicationWithArts, IContestForSelect } from '../../../services/Contests/interfaces'
import { resolveArtMeta } from '../Contests/artFieldHelpers'

type IArtSubmission = IContestApplicationWithArts['art_submissions'][number]

const POLLING_INTERVAL_MS = 5000

interface IContestsSendEmailsModalProps {
  isOpen: boolean
  onClose: () => void
  preSelectedContestName?: string
}

/**
 * Notificação por e-mail da inscrição: só existe nesta tela, então o mapa fica aqui — mas na
 * forma `{ label, colorScheme }` que o `<StatusBadge>` lê, e não num par de `Record` paralelos.
 */
const EMAIL_NOTIFICATION_STATUS: StatusMap = {
  not_sent: { label: 'Não Enviado', colorScheme: 'red' },
  pending: { label: 'Pendente', colorScheme: 'yellow' },
  sending: { label: 'Enviando', colorScheme: 'yellow' },
  sent: { label: 'Enviado', colorScheme: 'green' },
  failed: { label: 'Falhou', colorScheme: 'red' }
}

export default function ContestsSendEmailsModal({
  isOpen,
  onClose,
  preSelectedContestName
}: IContestsSendEmailsModalProps) {
  const contestService = useMemo(() => new ContestsService(), [])
  const [contests, setContests] = useState<IContestForSelect[]>([])
  const toast = useAppToast()
  const [selectedContest, setSelectedContest] = useState<number>(0)
  const [contestId, setContestId] = useState<number>(0)
  const [contestApplications, setContestApplications] = useState<IContestApplicationWithArts[]>([])
  const [selectedArt, setSelectedArt] = useState<IArtSubmission | null>(null)
  const artModal = useDisclosure()
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [selectedApplicationsIds, setSelectedApplicationsIds] = useState<number[]>([])
  const [pollingIds, setPollingIds] = useState<number[]>([])
  const pollingIdsRef = useRef<number[]>([])
  const prevPollingIdsLengthRef = useRef(0)
  const [searchText, setSearchText] = useState('')
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    pollingIdsRef.current = pollingIds
  }, [pollingIds])

  /**
   * Show a toast when polling finishes (all emails sent or failed).
   */
  useEffect(() => {
    if (pollingIds.length === 0 && prevPollingIdsLengthRef.current > 0) {
      toast.success('Envio de e-mails finalizado!')
      setSending(false)
    }
    prevPollingIdsLengthRef.current = pollingIds.length
  }, [pollingIds, toast])

  /**
   * Effect responsible for polling the email notification status of the selected applications.
   */
  useEffect(() => {
    if (pollingIds.length === 0) return

    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout>

    const poll = async () => {
      try {
        const results = await contestService.getEmailNotificationStatus(pollingIdsRef.current)

        setContestApplications((prev) =>
          prev.map((app) => {
            const result = results.find((r) => r.application_id === app.id)
            return result ? { ...app, email_notification_status: result.email_notification_status } : app
          })
        )

        const finishedIds = results
          .filter((r) => r.email_notification_status === 'sent' || r.email_notification_status === 'failed')
          .map((r) => r.application_id)

        if (finishedIds.length > 0) {
          setPollingIds((prev) => prev.filter((id) => !finishedIds.includes(id)))
        }
      } catch {
        // silently ignore polling errors
      }

      if (!cancelled && pollingIdsRef.current.length > 0) {
        timeoutId = setTimeout(poll, POLLING_INTERVAL_MS)
      }
    }

    timeoutId = setTimeout(poll, POLLING_INTERVAL_MS)

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [pollingIds, contestService])

  useEffect(() => {
    if (isOpen) {
      contestService
        .getAllForSelect()
        .then((response) => {
          setContests(response)
          // Pre-select contest if provided
          if (preSelectedContestName) {
            const matchedContest = response.find((c) => c.name === preSelectedContestName)
            if (matchedContest) {
              setSelectedContest(matchedContest.id)
            }
          }
        })
        .catch(() => toast.error('Erro ao Listar Concursos!'))
    } else {
      setSelectedContest(0)
      setContestId(0)
      setContestApplications([])
      setSelectedApplicationsIds([])
      setPollingIds([])
      setSearchText('')
    }
  }, [isOpen, preSelectedContestName, contestService, toast])

  /**
   * Fetch the applications of the selected contest.
   */
  const handleConfirmContest = () => {
    setLoading(true)

    setContestId(selectedContest)
    contestService
      .listApplicationsByContest(selectedContest)
      .then((response) => setContestApplications(response))
      .catch(() => toast.error('Erro ao Listar Inscrições!'))
      .finally(() => setLoading(false))
  }

  /**
   * Verify if all checked applications arts have been evaluated (approved or rejected).
   * @returns {boolean} True if all arts have been evaluated, false otherwise.
   */
  const isSelectedApplicationsAvailable = (): boolean => {
    const selectedApplications = contestApplications.filter((application) =>
      selectedApplicationsIds.some((selectedApplicationId) => selectedApplicationId === application.id)
    )

    // DEV-492 — stand_by counts as undecided alongside pending; e-mail só
    // é liberado quando todas as obras têm verdict (aprovada ou reprovada).
    return selectedApplications.every((application) =>
      application.art_submissions.every(
        (art) => art.exhibition_status !== 'pending' && art.exhibition_status !== 'stand_by'
      )
    )
  }

  /**
   * Handle the send emails action. It sends email notifications to the applicants of the selected applications
   * and starts polling each one until the backend confirms the email was sent.
   */
  const handleSendEmails = () => {
    const idsToSend = [...selectedApplicationsIds]
    setSending(true)

    contestService
      .sendEmailNotifications(idsToSend)
      .then(() => {
        toast.success('E-mails enviados com sucesso!')
        setSelectedApplicationsIds([])
        setPollingIds(idsToSend)
      })
      .catch(() => {
        toast.error('Erro ao enviar e-mails!')
        setSending(false)
      })
  }

  /**
   * Handle search input with debounce - filtering happens in render via searchText
   */
  const handleSearchChange = (value: string) => {
    setSearchText(value)
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      // Debounce complete - filtering happens in render via searchText
    }, 300)
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title="Enviar e-mails para inscritos"
        size="wide"
        footer={
          <>
            {contestId > 0 && (
              <Checkbox
                isChecked={contestApplications
                  .filter((app) => app.applicant_name.toLowerCase().includes(searchText.toLowerCase()))
                  .every((app) => selectedApplicationsIds.includes(app.id))}
                onChange={(e) => {
                  const filteredApps = contestApplications.filter((app) =>
                    app.applicant_name.toLowerCase().includes(searchText.toLowerCase())
                  )
                  if (e.target.checked) {
                    setSelectedApplicationsIds([
                      ...selectedApplicationsIds,
                      ...filteredApps.filter((app) => !selectedApplicationsIds.includes(app.id)).map((app) => app.id)
                    ])
                  } else {
                    setSelectedApplicationsIds(
                      selectedApplicationsIds.filter((id) => !filteredApps.some((app) => app.id === id))
                    )
                  }
                }}
              >
                Selecionar Todos
              </Checkbox>
            )}

            <Button
              variant="magenta"
              onClick={handleSendEmails}
              type="button"
              isLoading={sending}
              loadingText="Enviando E-mails..."
              isDisabled={
                contestId === 0 || selectedApplicationsIds.length === 0 || !isSelectedApplicationsAvailable() || sending
              }
            >
              Enviar e-mails
            </Button>
          </>
        }
      >
        {/* Contest Select */}
        <Flex gap={3} justifyContent={'space-between'}>
          <FormSelect
            isDisabled={contestId > 0}
            value={selectedContest}
            onChange={(value) => setSelectedContest(Number(value))}
            options={contests.map((contest) => ({ value: contest.id, label: contest.name }))}
            placeholder="Selecione um concurso"
          />

          {contestId === 0 ? (
            <Button onClick={handleConfirmContest} isDisabled={selectedContest === 0}>
              Selecionar
            </Button>
          ) : (
            <Button
              onClick={() => {
                setSelectedContest(0)
                setContestId(0)
                setContestApplications([])
                setSelectedApplicationsIds([])
                setPollingIds([])
                setSearchText('')
              }}
              isDisabled={sending}
            >
              Cancelar
            </Button>
          )}
        </Flex>

        {/* Alerts */}
        <Box display={'flex'} flexDirection={'column'} gap={3}>
          {contestId === 0 && (
            <Alert status="info" rounded={'lg'}>
              <AlertIcon />
              Selecione um concurso para visualizar os inscritos e enviar os e-mails de notificação.
            </Alert>
          )}

          {!loading &&
            contestId > 0 &&
            contestApplications.length > 0 &&
            selectedApplicationsIds.length > 0 &&
            !isSelectedApplicationsAvailable() && (
              <Alert status="error" rounded={'lg'}>
                <AlertIcon />É necessário aprovar ou reprovar todas as artes submetidas nas inscrições selecionadas.
              </Alert>
            )}

          {!loading && contestId > 0 && contestApplications.length === 0 && (
            <Alert status="warning" rounded={'lg'}>
              <AlertIcon />
              Nenhuma inscrição paga com artes enviadas encontrada para este concurso.
            </Alert>
          )}
        </Box>

        {/* Search Input - appears only after selecting contest */}
        {contestId > 0 && (
          <Input
            placeholder="Buscar por nome do aluno..."
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            size="md"
            py={2}
          />
        )}

        {/* Contest Application Cards */}
        <VStack w="100%" spacing={4}>
          {contestApplications
            .filter((app) => app.applicant_name.toLowerCase().includes(searchText.toLowerCase()))
            .map((application) => (
              <Box key={application.id} w="100%" borderWidth="1px" borderRadius="lg" overflow="hidden" boxShadow="sm">
                {/* Student row */}
                <Flex
                  align="center"
                  gap={3}
                  px={4}
                  py={3}
                  onClick={() => {
                    if (
                      selectedApplicationsIds.some((selectedApplicationId) => selectedApplicationId === application.id)
                    ) {
                      setSelectedApplicationsIds(selectedApplicationsIds.filter((id) => id !== application.id))
                    } else {
                      setSelectedApplicationsIds([...selectedApplicationsIds, application.id])
                    }
                  }}
                  cursor="pointer"
                >
                  <Avatar src={application.applicant_image_url} size="sm" />

                  <Box flex={1}>
                    <Text fontWeight="semibold" fontSize="sm">
                      {application.applicant_name}
                    </Text>
                    <Text variant="caption">
                      {application.course.name} · {application.teacher.name}
                    </Text>
                  </Box>

                  <Text>Status da notificação: </Text>
                  <StatusBadge
                    status={application.email_notification_status}
                    statuses={EMAIL_NOTIFICATION_STATUS}
                    textTransform={'capitalize'}
                  />

                  <Checkbox
                    isChecked={selectedApplicationsIds.some(
                      (selectedApplicationId) => selectedApplicationId === application.id
                    )}
                    pointerEvents="none"
                  />
                </Flex>

                {/* Arts row */}
                <Flex gap={3} px={4} py={3} overflowX="auto">
                  {application.art_submissions.map((art) => (
                    <Box
                      key={art.id}
                      flexShrink={0}
                      w="140px"
                      borderWidth="1px"
                      borderRadius="md"
                      overflow="hidden"
                      cursor="pointer"
                      transition="all 0.15s"
                      _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
                      onClick={() => {
                        setSelectedArt(art)
                        artModal.onOpen()
                      }}
                    >
                      <Image src={art.art_image_url} alt={art.name} w="140px" h="100px" objectFit="contain" />

                      <Flex direction="column" gap={1} px={2} py={2}>
                        <Box fontSize="xs" fontWeight="medium" noOfLines={1}>
                          {art.name}
                        </Box>

                        <StatusBadge
                          status={art.exhibition_status}
                          statuses={EXHIBITION_STATUS}
                          w="fit-content"
                          mx={'auto'}
                        />
                      </Flex>
                    </Box>
                  ))}
                </Flex>
              </Box>
            ))}
        </VStack>
      </FormModal>

      {/* Art Modal */}
      {selectedArt && <ArtModal isOpen={artModal.isOpen} onClose={artModal.onClose} artSubmission={selectedArt} />}
    </>
  )
}

/**
 * ArtModal component displays detailed information about an art submission.
 */
function ArtModal({
  isOpen,
  onClose,
  artSubmission
}: {
  isOpen: boolean
  onClose: () => void
  artSubmission: IArtSubmission
}) {
  // `exhibition_status` é `string` no tipo aninhado, não a união — e com `noUncheckedIndexedAccess`
  // desligado o TS devolve `IStatusConfig` sem avisar. Continua sendo um `<Tag>` e não um
  // `<StatusBadge>` porque o campo irmão ("Leilão") é um `<Tag>` do mesmo formato: converter só um
  // deixaria duas pílulas diferentes lado a lado.
  const status = EXHIBITION_STATUS[artSubmission.exhibition_status] ?? {
    label: artSubmission.exhibition_status,
    colorScheme: 'gray'
  }

  return (
    <FormModal isOpen={isOpen} onClose={onClose} size="wide">
      <Grid templateColumns="2fr 1fr" minH="400px">
        {/* Left: Image */}
        <GridItem bg="gray.50" display="flex" alignItems="center" justifyContent="center" p={6}>
          <Image
            src={artSubmission.art_image_url}
            alt={artSubmission.name}
            maxH="560px"
            maxW="100%"
            objectFit="contain"
            borderRadius="md"
          />
        </GridItem>

        {/* Right: Info */}
        <GridItem p={6} display="flex" flexDirection="column" gap={4} overflowY="auto">
          <Box>
            <Text fontSize="xl" fontWeight="bold" lineHeight="short">
              {artSubmission.name}
            </Text>
          </Box>

          {resolveArtMeta(artSubmission).map((meta) => (
            <Box key={meta.key}>
              <Divider />
              <DetailField label={meta.label}>
                <Text
                  fontSize="sm"
                  color={meta.answered ? undefined : 'gray.400'}
                  fontStyle={meta.answered ? undefined : 'italic'}
                >
                  {meta.value}
                </Text>
              </DetailField>
            </Box>
          ))}

          <Divider />

          <DetailField label="Leilão">
            <Tag colorScheme={artSubmission.available_for_auction ? 'green' : 'gray'} borderRadius="full" px={2}>
              {artSubmission.available_for_auction ? 'Habilitada' : 'Não disponível para venda'}
            </Tag>
          </DetailField>

          <Divider />

          <DetailField label="Status de exibição">
            <Tag colorScheme={status.colorScheme} borderRadius="full" px={2}>
              {status.label}
            </Tag>
          </DetailField>
        </GridItem>
      </Grid>
    </FormModal>
  )
}
